import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function description(tab, subscribing) {
  if (subscribing) return "Your subscription is tied to your account so you can cancel or manage it from any device. Sign in or create a free account — we'll take you straight to checkout.";
  if (tab === "login") return "Sign in to manage your subscription, save picks and see your history.";
  return "Create a free account. No email verification needed.";
}

function submitLabel(tab, subscribing, busy) {
  if (busy) return "...";
  if (tab === "login") return subscribing ? "Sign in & continue to checkout" : "Sign In";
  return subscribing ? "Create account & continue" : "Create Account";
}

export function AuthModal({ open, onOpenChange, subscribing = false }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(tab === "login" ? "Welcome back" : "Account created");
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-modal"
        className="bg-surface border-white/10 text-parchment max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl tracking-tight">
            {tab === "login" ? "Welcome back" : "Join the Oracle"}
          </DialogTitle>
          <DialogDescription data-testid="auth-modal-description" className="font-body text-parchment/60 text-sm">
            {description(tab, subscribing)}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 bg-ink border border-white/10">
            <TabsTrigger data-testid="tab-login" value="login" className="data-[state=active]:bg-oracle data-[state=active]:text-ink label-caps">
              Login
            </TabsTrigger>
            <TabsTrigger data-testid="tab-register" value="register" className="data-[state=active]:bg-oracle data-[state=active]:text-ink label-caps">
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="space-y-4 pt-4">
            <Field label="Email" testid="login-email" value={email} onChange={setEmail} type="email" />
            <Field label="Password" testid="login-password" value={password} onChange={setPassword} type="password" />
          </TabsContent>
          <TabsContent value="register" className="space-y-4 pt-4">
            <Field label="Name (optional)" testid="reg-name" value={name} onChange={setName} />
            <Field label="Email" testid="reg-email" value={email} onChange={setEmail} type="email" />
            <Field label="Password (min 6)" testid="reg-password" value={password} onChange={setPassword} type="password" />
          </TabsContent>
        </Tabs>
        <Button
          data-testid="auth-submit-btn"
          disabled={busy || !email || password.length < 6}
          onClick={submit}
          className="w-full bg-oracle text-ink hover:bg-[#FFC033] label-caps"
        >
          {submitLabel(tab, subscribing, busy)}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="label-caps">{label}</Label>
      <Input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-ink border-white/10 text-parchment font-body focus-visible:ring-oracle"
      />
    </div>
  );
}
